import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, Calendar, CreditCard, UserCog, BarChart3, LogOut, Utensils, Menu, X, FolderKanban, KeyRound, FileText, Settings } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import ChangePasswordModal from "@/components/ChangePasswordModal";

const items = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/clientes", label: "Clientes", icon: Users, id: "nav-clientes" },
  { to: "/proyectos", label: "Proyectos", icon: FolderKanban, id: "nav-proyectos" },
  { to: "/agenda", label: "Agenda", icon: Calendar, id: "nav-agenda" },
  { to: "/pagos", label: "Pagos", icon: CreditCard, id: "nav-pagos" },
  { to: "/documentos", label: "Documentos", icon: FileText, id: "nav-documentos" },
  { to: "/equipo", label: "Equipo", icon: UserCog, id: "nav-equipo" },
  { to: "/reportes", label: "Reportes", icon: BarChart3, id: "nav-reportes" },
  { to: "/configuracion", label: "Configuración", icon: Settings, id: "nav-configuracion" },
];

const NavItems = ({ onNavigate }) => (
  <>
    {items.map(({ to, label, icon: Icon, id }) => (
      <NavLink
        key={to}
        to={to}
        data-testid={id}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors duration-200 ${
            isActive
              ? "bg-[#F97316]/10 text-[#F97316]"
              : "text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#15171E]"
          }`
        }
      >
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
        <span className="text-sm font-medium" style={{ fontFamily: "Inter, sans-serif" }}>{label}</span>
      </NavLink>
    ))}
  </>
);

const Brand = () => (
  <div className="flex items-center gap-3">
    <div className="w-10 h-10 rounded-xl bg-[#F97316] flex items-center justify-center">
      <Utensils className="w-5 h-5 text-white" strokeWidth={2} />
    </div>
    <div>
      <div className="font-bold text-[#F5F5F7] text-[15px] leading-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
        Focus Marketing
      </div>
      <div className="text-[11px] text-[#9CA3AF]">El Digital de Restaurantes</div>
    </div>
  </div>
);

const ProfileBlock = ({ user, onLogout, onChangePassword }) => (
  <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-[#15171E]">
    {user?.picture ? (
      <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" />
    ) : (
      <div className="w-8 h-8 rounded-full bg-[#262A33] flex items-center justify-center text-[#F5F5F7] text-xs font-bold">
        {(user?.name || "U").charAt(0).toUpperCase()}
      </div>
    )}
    <div className="flex-1 min-w-0">
      <div className="text-[13px] font-medium text-[#F5F5F7] truncate">{user?.name || "Usuario"}</div>
      <div className="text-[11px] text-[#9CA3AF] truncate">{user?.email}</div>
    </div>
    <button
      data-testid="change-password-btn"
      onClick={onChangePassword}
      className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F97316] hover:bg-[#262A33] transition-colors"
      aria-label="Cambiar contraseña"
      title="Cambiar contraseña"
    >
      <KeyRound className="w-4 h-4" strokeWidth={1.75} />
    </button>
    <button
      data-testid="logout-btn"
      onClick={onLogout}
      className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#262A33] transition-colors"
      aria-label="Cerrar sesión"
    >
      <LogOut className="w-4 h-4" strokeWidth={1.75} />
    </button>
  </div>
);

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const handleChangePassword = () => { setMobileOpen(false); setPwdOpen(true); };

  return (
    <>
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-30 bg-[#0B0D12]/95 backdrop-blur border-b border-[#262A33]">
        <div className="flex items-center justify-between px-4 py-3">
          <Brand />
          <button
            data-testid="mobile-menu-btn"
            onClick={()=>setMobileOpen(true)}
            className="p-2 rounded-xl bg-[#15171E] border border-[#262A33] text-[#F5F5F7] hover:bg-[#1A1D24] transition-colors"
            aria-label="Abrir menú"
          >
            <Menu className="w-5 h-5" strokeWidth={1.75}/>
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={()=>setMobileOpen(false)}/>
          <aside className="absolute left-0 top-0 h-full w-72 bg-[#0B0D12] border-r border-[#262A33] flex flex-col shadow-2xl">
            <div className="flex items-center justify-between px-4 pt-4 pb-6">
              <Brand />
              <button
                data-testid="mobile-close-btn"
                onClick={()=>setMobileOpen(false)}
                className="p-2 rounded-xl text-[#9CA3AF] hover:text-[#F5F5F7] hover:bg-[#15171E] transition-colors"
                aria-label="Cerrar menú"
              >
                <X className="w-5 h-5" strokeWidth={1.75}/>
              </button>
            </div>
            <nav className="flex-1 px-3 space-y-1">
              <NavItems onNavigate={()=>setMobileOpen(false)}/>
            </nav>
            <div className="p-3 border-t border-[#262A33]">
              <ProfileBlock user={user} onLogout={handleLogout} onChangePassword={handleChangePassword}/>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[#0B0D12] border-r border-[#262A33] fixed left-0 top-0 h-screen z-20">
        <div className="px-6 pt-6 pb-8">
          <Brand />
        </div>
        <nav className="flex-1 px-3 space-y-1">
          <NavItems />
        </nav>
        <div className="p-3 border-t border-[#262A33]">
          <ProfileBlock user={user} onLogout={handleLogout} onChangePassword={handleChangePassword}/>
        </div>
      </aside>

      <ChangePasswordModal open={pwdOpen} onOpenChange={setPwdOpen}/>
    </>
  );
}
